// frontend/src/components/engineer/modals/AddMoreEmployeesModal.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  PlusCircleIcon,
  UserPlusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import {
  overtimeManagementService,
  OvertimeRequestSummary
} from '../../../services/overtimeManagement.service';
import { Employee } from '../../../services/createOvertime.service';
import { AuthUser } from '../../../services/auth.service';
import styles from './AddMoreEmployeesModal.module.css';

interface AddMoreEmployeesModalProps {
  requestSummary: OvertimeRequestSummary;
  user: AuthUser;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

const AddMoreEmployeesModal: React.FC<AddMoreEmployeesModalProps> = ({
  requestSummary,
  user,
  onClose,
  onSuccess
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Employee[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = user?.employeeNo || user?.employeeNumber || user?.uid || '';
  const userName = user?.name || 'Unknown User';

  // Search employees as user types
  useEffect(() => {
    const searchTimeout = setTimeout(() => {
      if (searchTerm.trim().length >= 2) {
        performSearch();
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchTerm]);

  const performSearch = async () => {
    try {
      setIsSearching(true);
      setError(null);

      const existingIds = requestSummary.employees.map(emp => emp.id);
      const results = await overtimeManagementService.searchEmployeesForAdding(
        searchTerm,
        requestSummary.overtimeDate,
        existingIds
      );

      setSearchResults(results);

      if (results.length === 0) {
        setError(`No employees found matching "${searchTerm}" with attendance on ${requestSummary.overtimeDate.toLocaleDateString()}`);
      }

    } catch (err) {
      console.error('Error searching employees:', err);
      setError('Failed to search employees');
    } finally {
      setIsSearching(false);
    }
  };

  const handleToggleEmployee = (employeeId: string) => {
    setSelectedEmployees(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleSubmit = async () => {
    if (selectedEmployees.length === 0) {
      setError('Please select at least one employee');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      let result;

      if (requestSummary.status === 'pending') {
        // Add to existing pending request
        result = await overtimeManagementService.addEmployeesToPendingRequest(
          requestSummary.requestId,
          selectedEmployees,
          userId,
          userName
        );
      } else if (requestSummary.status === 'approved') {
        // Create new request for approved overtime
        result = await overtimeManagementService.createAdditionalRequestForApproved(
          requestSummary.requestId,
          selectedEmployees,
          userId,
          userName
        );
      } else {
        setError('Cannot add employees to rejected or cancelled requests');
        return;
      }

      if (result.success) {
        onSuccess(result.message);
        onClose();
      } else {
        setError(result.message);
      }

    } catch (err) {
      console.error('Error adding employees:', err);
      setError(err instanceof Error ? err.message : 'Failed to add employees');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerIcon}>
            <UserPlusIcon />
          </div>
          <div className={styles.headerText}>
            <h2>Add More Employees</h2>
            <p>
              {requestSummary.status === 'pending'
                ? 'Add employees to existing request'
                : 'Create new request for additional employees'}
            </p>
          </div>
          <button onClick={onClose} className={styles.closeButton}>
            <XMarkIcon />
          </button>
        </div>

        {/* Request Summary */}
        <div className={styles.requestSummary}>
          <div className={styles.summaryHeader}>
            <h3>Current Request Details</h3>
            <span className={`${styles.statusBadge} ${styles[requestSummary.status]}`}>
              {requestSummary.status}
            </span>
          </div>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Project:</span>
              <span className={styles.summaryValue}>{requestSummary.projectName}</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Date:</span>
              <span className={styles.summaryValue}>
                {requestSummary.overtimeDate.toLocaleDateString()}
              </span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Current Employees:</span>
              <span className={styles.summaryValue}>{requestSummary.employees.length}</span>
            </div>
          </div>

          {/* Current Employees List */}
          <div className={styles.currentEmployees}>
            <h4>Current Employees:</h4>
            <div className={styles.employeeTags}>
              {requestSummary.employees.slice(0, 5).map(emp => (
                <span key={emp.id} className={styles.employeeTag}>
                  {emp.name}
                </span>
              ))}
              {requestSummary.employees.length > 5 && (
                <span className={styles.moreTag}>
                  +{requestSummary.employees.length - 5} more
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Status Notice */}
        {requestSummary.status === 'approved' && (
          <div className={styles.noticeCard} style={{ background: '#fef3c7', borderColor: '#f59e0b' }}>
            <ExclamationTriangleIcon style={{ width: '1.25rem', height: '1.25rem', color: '#d97706' }} />
            <div>
              <strong>Creating New Request</strong>
              <p>Since the original request is approved, adding more employees will create a new overtime request that requires approval.</p>
            </div>
          </div>
        )}

        {requestSummary.status === 'pending' && (
          <div className={styles.noticeCard} style={{ background: '#f0f9ff', borderColor: '#0ea5e9' }}>
            <CheckCircleIcon style={{ width: '1.25rem', height: '1.25rem', color: '#0284c7' }} />
            <div>
              <strong>Updating Existing Request</strong>
              <p>Employees will be added to your pending request. No new approval needed.</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className={styles.errorMessage}>
            <ExclamationTriangleIcon />
            <span>{error}</span>
          </div>
        )}

        {/* Search Section */}
        <div className={styles.searchSection}>
          <h3>Search Employees</h3>
          <p className={styles.searchHint}>
            Search by name or PIN number (only employees with attendance on{' '}
            {requestSummary.overtimeDate.toLocaleDateString()})
          </p>

          <div className={styles.searchContainer}>
            <MagnifyingGlassIcon className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search by name or PIN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
              autoFocus
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className={styles.clearButton}
              >
                <XMarkIcon />
              </button>
            )}
          </div>

          {/* Search Results */}
          {isSearching && (
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <p>Searching employees...</p>
            </div>
          )}

          {!isSearching && searchResults.length > 0 && (
            <div className={styles.searchResults}>
              <div className={styles.resultsHeader}>
                <h4>Found {searchResults.length} employee(s)</h4>
                {selectedEmployees.length > 0 && (
                  <span className={styles.selectedCount}>
                    {selectedEmployees.length} selected
                  </span>
                )}
              </div>
              
              <div className={styles.employeeList}>
                {searchResults.map(employee => (
                  <label
                    key={employee.id}
                    className={`${styles.employeeCard} ${
                      selectedEmployees.includes(employee.id) ? styles.selected : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEmployees.includes(employee.id)}
                      onChange={() => handleToggleEmployee(employee.id)}
                      className={styles.checkbox}
                    />
                    <div className={styles.employeeInfo}>
                      <div className={styles.employeeName}>{employee.name}</div>
                      <div className={styles.employeeDetails}>
                        PIN: {employee.pin || employee.employeeNumber || 'N/A'}
                      </div>
                      <div className={styles.employeeDetails}>
                        {employee.designation} • {employee.department}
                      </div>
                      <div className={styles.attendanceStatus}>
                        ✓ Has Attendance
                      </div>
                    </div>
                    {selectedEmployees.includes(employee.id) && (
                      <CheckCircleIcon className={styles.selectedIcon} />
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {!isSearching && searchTerm.length >= 2 && searchResults.length === 0 && (
            <div className={styles.emptyState}>
              <p>No employees found with attendance on this date</p>
              <small>Try searching by name or PIN number</small>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className={styles.modalFooter}>
          <button
            onClick={onClose}
            className={styles.cancelButton}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={selectedEmployees.length === 0 || isSubmitting}
            className={styles.submitButton}
          >
            {isSubmitting ? (
              <>
                <div className={styles.submitSpinner}></div>
                {requestSummary.status === 'pending' ? 'Adding...' : 'Creating...'}
              </>
            ) : (
              <>
                <PlusCircleIcon className={styles.buttonIcon} />
                {requestSummary.status === 'pending'
                  ? `Add ${selectedEmployees.length} Employee(s)`
                  : `Create Request for ${selectedEmployees.length} Employee(s)`}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMoreEmployeesModal;